import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function BudgetPlanner() {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: '', amount: '' });

  const categories = ['Housing', 'Food', 'Transport', 'Utilities', 'Entertainment', 'Healthcare', 'Education', 'Shopping', 'Travel', 'Savings', 'Other'];

  useEffect(() => { fetchBudgets(); }, []);

  const fetchBudgets = async () => {
    try {
      const res = await api.get('/budgets');
      const data = res.data?.data || res.data || [];
      setBudgets(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Failed to load budgets'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount) return toast.error('Fill all fields');
    try {
      await api.post('/budgets', form);
      toast.success(editingId ? 'Budget updated!' : 'Budget created!');
      setShowForm(false); setEditingId(null); setForm({ category: '', amount: '' });
      fetchBudgets();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget?')) return;
    try { await api.delete('/budgets/' + id); toast.success('Deleted'); fetchBudgets(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);
  const totalBudget = budgets.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budget Planner</h1>
          <p className="text-gray-500">Total Budget: <span className="text-indigo-600 font-bold">{formatCurrency(totalBudget)}</span></p>
        </div>
        <button onClick={() => { setEditingId(null); setForm({ category: '', amount: '' }); setShowForm(!showForm); }}
          className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
          {showForm ? '✕ Cancel' : '+ New Budget'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-slideDown">
          <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Budget' : 'Create New Budget'}</h3>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} required
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
              <option value="">Select Category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            <button type="submit" className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 font-medium">
              {editingId ? 'Update' : 'Create'}
            </button>
          </form>
        </div>
      )}

      {budgets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="text-5xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900">No Budgets Yet</h3>
          <p className="text-gray-500 mt-1">Create your first budget to start tracking spending limits</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.map(b => (
            <div key={b._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-3">
                <span className="inline-flex px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">{b.category}</span>
                <div className="flex gap-1">
                  <button onClick={() => { setEditingId(b._id); setForm({ category: b.category, amount: b.amount }); setShowForm(true); }}
                    className="text-gray-400 hover:text-indigo-600 p-1">✏️</button>
                  <button onClick={() => handleDelete(b._id)} className="text-gray-400 hover:text-red-600 p-1">🗑️</button>
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(b.amount)}</p>
              <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: Math.min((b.spent || 0) / b.amount * 100, 100) + '%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Spent: {formatCurrency(b.spent || 0)} / {formatCurrency(b.amount)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
